import { ColumnsQueryParams } from '@ui/table-modifier/ColumnsModal';
import { COPY_SPECIFIC } from '@sonar-software/sonar-ts-constants';
import { createAsyncSelectors } from '@lib/selectors';
import { DataDictionarySearchQueryParams } from './DataDictionarySearch';
import { getSonarObjectTypeId } from '@sonar-software/sonar-ts-lib';
import { QuickAdd } from '@ui/general';
import { ViewForm } from './ViewForm';

import { coreActions, DataDictionaryBaseRequest } from '@sonar-software/sonar-core';
import React, { useEffect, useRef } from 'react';
import { useAction, useAsyncState, useOnUnmount } from '@hooks';

export type ViewQueryParams = DataDictionaryBaseRequest &
	DataDictionarySearchQueryParams &
	ColumnsQueryParams & {
		lastViewId?: number;
		viewName?: string;
		viewSkip?: boolean;
	};

export interface CreateNewViewProps {
	onFinish: (viewId: number, viewName: string) => void;
	queryParams: ViewQueryParams;
}

const createViewSelectors = createAsyncSelectors((state) => state.core.dataDictionary.views.post);

export const CreateView: React.FC<CreateNewViewProps> = ({ onFinish, queryParams }) => {
	const { data, isReceived, isRequested } = useAsyncState(createViewSelectors);

	const createView = useAction(coreActions.requestPostCoreDataDictionaryViews);
	const resetCreate = useAction(coreActions.resetPostCoreDataDictionaryViews);

	const nameRef = useRef('');

	useOnUnmount(resetCreate);

	const onFinishRef = useRef(onFinish);
	onFinishRef.current = onFinish;

	useEffect(() => {
		if (isReceived && data) {
			onFinishRef.current(data.viewId, nameRef.current);
		}
	}, [ data, isReceived ]);

	return (
		<QuickAdd
			form={ ViewForm }
			onSubmit={ (viewName) => {
				nameRef.current = viewName;

				createView({
					applicationId: queryParams.applicationId,
					columns: queryParams.columns ?? '',
					complianceCategory: queryParams.complianceCategory,
					filter: queryParams.filters ?? '',
					ordering: queryParams.ordering ?? '',
					sonarObjectId: queryParams.sonarObjectId,
					sonarObjectTypeId: queryParams.sonarObjectType ?
						getSonarObjectTypeId(queryParams.sonarObjectType) :
						undefined,
					tagId: queryParams.tagId,
					viewName: viewName?.trim()
				});
			} }
			processing={ isRequested }
			toggleProps={ { id: 'save-filters-new-view-button' } }
			typeLabel={ COPY_SPECIFIC.VIEW } />
	);
};
